import http from './http.service';
import {AppSettings} from "@/shared/AppSettings";

class SsoService {
  /**
   * Check if SSO is enabled for current domain
   * @returns {Promise<{enabled: boolean, tenant_id?: string, client_id?: string, redirect_uri?: string}>}
   */
  async getSsoConfig() {
    // Use host (includes port) instead of hostname (no port)
    const domain = window.location.host;
    console.log('SSO Service: Requesting config for domain:', domain);
    
    try {
      const {body, success, response} = await http.get(
        AppSettings.getUrl(`/api/v1/users/sso-config/?domain=${encodeURIComponent(domain)}`)
      );
      
      console.log('SSO Service: Response success:', success, 'Body:', body);
      
      if (success && body && body.enabled) {
        return {
          enabled: true,
          tenant_id: body.tenant_id,
          client_id: body.client_id,
          redirect_uri: body.redirect_uri
        };
      }
      
      console.log('SSO Service: SSO not enabled or config not found');
      return {enabled: false};
    } catch (error) {
      console.error('SSO Service: Error fetching config:', error);
      return {enabled: false};
    }
  }

  /**
   * Build Entra ID authorization URL
   * @param {string} tenantId - Azure AD Tenant ID
   * @param {string} clientId - Azure AD Application Client ID
   * @param {string} redirectUri - OAuth redirect URI
   * @param {string} state - State parameter for CSRF protection
   * @returns {string} Authorization URL
   */
  buildAuthUrl(tenantId, clientId, redirectUri, state) {
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      redirect_uri: redirectUri,
      response_mode: 'query',
      scope: 'openid profile email User.Read',
      state: state
    });
    
    return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
  }

  /**
   * Generate random state parameter for CSRF protection
   * @returns {string} Random state string
   */
  generateState() {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Initiate SSO flow - redirect to Entra ID
   * @param {Object} config - SSO config with tenant_id, client_id, redirect_uri
   */
  initiateSso(config) {
    const state = this.generateState();
    
    // Store state in sessionStorage for validation on callback
    sessionStorage.setItem('sso_state', state);
    
    // Build frontend callback URL (redirect_uri should point to frontend)
    const frontendCallbackUrl = `${window.location.protocol}//${window.location.host}/auth/sso/callback`;
    
    const authUrl = this.buildAuthUrl(
      config.tenant_id,
      config.client_id,
      frontendCallbackUrl,  // Use frontend callback URL
      state
    );
    
    // Redirect to Entra ID
    window.location.href = authUrl;
  }

  /**
   * Handle SSO callback - extract code from URL and send to backend
   * @returns {Promise<{success: boolean, token?: string, error?: string}>}
   */
  async handleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const error_description = urlParams.get('error_description');
    
    // Handle OAuth errors
    if (error) {
      const errorMsg = error_description || error;
      return {
        success: false,
        error: decodeURIComponent(errorMsg)
      };
    }
    
    if (!code) {
      return {
        success: false,
        error: 'Authorization code is missing'
      };
    }
    
    // Validate state (CSRF protection)
    const storedState = sessionStorage.getItem('sso_state');
    if (state && storedState && state !== storedState) {
      return {
        success: false,
        error: 'Invalid state parameter. Possible CSRF attack.'
      };
    }
    
    // Clean up state from sessionStorage
    sessionStorage.removeItem('sso_state');
    
    // Send code to backend
    const domain = window.location.host;
    const {body, success} = await http.post(
      AppSettings.getUrl('/api/v1/users/sso/callback/'),
      {
        code: code,
        state: state,
        domain: domain
      }
    );
    
    if (success && body.token) {
      return {
        success: true,
        token: body.token
      };
    }
    
    return {
      success: false,
      error: body.error || 'SSO authentication failed'
    };
  }
}

export default new SsoService();
